import React from 'react';
import { Check, ShieldCheck } from 'lucide-react';
import Alt6Layout from '../components/Alt6Layout';
import {
  CtaBand,
  Eyebrow,
  Reveal,
  SectionHead,
} from '../components/Alt6Shared';
import {
  AI_CAPABILITIES,
  AUTOPILOT_CHECKLIST,
  GUARDRAILS,
  LOOP_STEPS,
} from '../data/site';

export default function Alt6Autopilot() {
  return (
    <Alt6Layout title="Autopilot">
      <section className="a6-page-hero">
        <div className="a6-container a6-page-hero-inner">
          <Reveal>
            <Eyebrow>Autopilot</Eyebrow>
          </Reveal>
          <Reveal delay={0.06}>
            <h1>
              AI that <em>does the work</em>, not just the talking
            </h1>
          </Reveal>
          <Reveal delay={0.12}>
            <p>
              At the core of Asha is ARIA — a reasoning engine that looks at the state
              of your practice, decides what needs to happen next, and dispatches
              specialist AI to do it. Not suggestions in a chat window. Completed work
              in your system of record.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="a6-section">
        <div className="a6-container">
          <SectionHead
            eyebrow="How it works"
            title="Reason. Act. Observe. Repeat."
            lede={'"Eight visits tomorrow still need verification" becomes eight verified visits — without anyone assigning the task.'}
          />
          <div className="a6-loop">
            {LOOP_STEPS.map((step, i) => (
              <Reveal key={step.name} delay={i * 0.08}>
                <div className="a6-loop-step">
                  <span className="a6-loop-num">{i + 1}</span>
                  <h3>{step.name}</h3>
                  <p>{step.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="a6-section a6-wash">
        <div className="a6-container">
          <SectionHead
            eyebrow="The AI workforce"
            title="Every department, covered"
            lede="Not one AI tool for one task — a full workforce across the front office, the revenue cycle, and clinical support, all working in the same patient record."
          />
          {AI_CAPABILITIES.map((group) => (
            <div className="a6-cap-group" key={group.area}>
              <Reveal className="a6-cap-label">
                <h3>{group.area}</h3>
                <span className="a6-cap-count">{group.items.length} capabilities</span>
              </Reveal>
              <div className="a6-grid a6-grid-3">
                {group.items.map((item, i) => (
                  <Reveal key={item.name} delay={(i % 3) * 0.06}>
                    <div className="a6-card">
                      <h3>{item.name}</h3>
                      <p>{item.body}</p>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="a6-section">
        <div className="a6-container">
          <Reveal className="a6-band">
            <Eyebrow>
              <ShieldCheck size={13} strokeWidth={2.6} style={{ marginRight: 2 }} />
              In your control
            </Eyebrow>
            <h2 className="a6-h2">Autopilot doesn't mean out of your hands.</h2>
            <p className="a6-lede">
              You decide what runs autonomously and what waits for sign-off — and you
              can see everything the AI did, said, and sent.
            </p>
            <div className="a6-vs" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
              {GUARDRAILS.map((g) => (
                <div className="a6-vs-card" key={g.title}>
                  <h3 style={{ fontSize: 19 }}>{g.title}</h3>
                  <p>{g.body}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="a6-section a6-wash">
        <div className="a6-container">
          <SectionHead
            eyebrow="The destination"
            title="The automation inflection point"
            lede="Autopilot ramps up module by module. You'll know you've crossed the threshold when all three of these are true."
            centered
          />
          <div className="a6-checklist">
            {AUTOPILOT_CHECKLIST.map((item, i) => (
              <Reveal key={item.title} delay={i * 0.08}>
                <div className="a6-check-card">
                  <span className="a6-check-mark">
                    <Check size={17} strokeWidth={3} />
                  </span>
                  <h3>{item.title}</h3>
                  <p>{item.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaBand
        title="Watch ARIA run a real workflow."
        body="Bring a messy Tuesday — we'll show you what autopilot does with it."
      />
    </Alt6Layout>
  );
}
